import { createElement, Fragment } from "react";
// Runtime for UI translation. Strings are written in English in the code and wrapped in t("…");
// locales/<lang>.json maps each English string to its translation ("_m" holds the marketing page copy).
// The language is chosen once per page load; changing it reloads the page (see setLang).
import { LANG_CODES, LOCALES, isLang } from "./languages";

export * from "./languages";

const STORE = "inm_lang";
let current = "en";
let catalog = {};

// Marketing pages carry the language in the URL: /, /es, /de/privacy …
export function urlLang(pathname = window.location.pathname) {
  const seg = pathname.split("/")[1];
  return isLang(seg) && seg !== "en" ? seg : null;
}

export function browserLang() {
  for (const raw of navigator.languages || [navigator.language || ""]) {
    const l = String(raw).toLowerCase().split("-")[0];
    const code = l === "nb" || l === "nn" ? "no" : l;
    if (isLang(code)) return code;
  }
  return "en";
}

export function storedLang() {
  try {
    const l = localStorage.getItem(STORE);
    return isLang(l) ? l : null;
  } catch {
    return null;
  }
}

export function rememberLang(l) {
  try {
    if (isLang(l)) localStorage.setItem(STORE, l);
  } catch {
    /* private mode */
  }
}

// ?lang=xx overrides, then a remembered choice, then the browser language.
export function preferredLang() {
  const q = new URLSearchParams(window.location.search).get("lang");
  if (isLang(q)) {
    rememberLang(q);
    return q;
  }
  return storedLang() || browserLang();
}

// Marketing pages and their language: "/" → en, "/de/terms" → de. null for any other page.
// Page keys are the English slugs; Spanish uses its own slug for two of them.
export const MARKETING_PAGES = ["", "privacy", "terms", "legal", "cookies", "dpa"];
const SLUGS = { es: { legal: "aviso-legal", dpa: "encargo-tratamiento" } };
const pageOfSlug = (lang, slug) => Object.keys(SLUGS[lang] || {}).find((k) => SLUGS[lang][k] === slug) ?? slug;
// Every language has marketing copy (locales/<lang>.json "_m").
export const MARKETING_LANGS = LANG_CODES;
// Pages whose text exists in every language; the others (legal texts) are binding in English and Spanish only.
const TRANSLATED_PAGES = ["", "cookies"];

// { lang, page } for a marketing URL, else null.
export function marketingPageOf(pathname) {
  const parts = pathname.replace(/\/+$/, "").split("/").slice(1);
  if (parts.length === 0) return { lang: "en", page: "" };
  if (parts.length === 1 && MARKETING_PAGES.includes(parts[0])) return { lang: "en", page: parts[0] };
  if (MARKETING_LANGS.includes(parts[0]) && parts[0] !== "en" && parts.length <= 2) {
    const page = pageOfSlug(parts[0], parts[1] || "");
    if (MARKETING_PAGES.includes(page) && !(parts[1] && SLUGS[parts[0]]?.[parts[1]])) return { lang: parts[0], page };
  }
  return null;
}
export const marketingLangOf = (pathname) => marketingPageOf(pathname)?.lang || null;

// Decide the language for this page load. Returns null when it redirected to the visitor's language instead.
export function bootLang({ agencyHost = false } = {}) {
  const { pathname, search, hash } = window.location;
  const seg = pathname.split("/");
  if (agencyHost) return isLang(seg[1]) ? seg[1] : "en"; // agency's own domain: /<lang>/…
  if (seg[1] === "s") return isLang(seg[3]) ? seg[3] : "en"; // agency site preview: /s/<slug>/<lang>/…
  const mp = marketingPageOf(pathname);
  if (mp && mp.lang !== "en") {
    rememberLang(mp.lang);
    return mp.lang;
  }
  const pref = preferredLang();
  if (mp && pref !== "en" && (pref === "es" || TRANSLATED_PAGES.includes(mp.page)) && !new URLSearchParams(search).get("lang")) {
    window.location.replace(marketingPath(pref, mp.page) + hash);
    return null;
  }
  if (mp) return "en";
  return pref;
}

export async function loadLang(l) {
  const lang = isLang(l) ? l : "en";
  try {
    catalog = (await import(/* webpackChunkName: "lang-[request]" */ `./locales/${lang}.json`)).default;
  } catch {
    catalog = {};
  }
  current = lang;
  document.documentElement.lang = lang;
  return lang;
}

export const getLang = () => current;
export const getLocale = () => LOCALES[current] || "en-GB";
export const marketingCopy = () => catalog._m;

// t("Saved {n} photos", { n: 3 })
export const hasT = (s) => Object.prototype.hasOwnProperty.call(catalog, s);
export function t(s, vars) {
  let out = (typeof s === "string" && catalog[s]) || s;
  if (vars && typeof out === "string") out = out.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m));
  return out;
}

// Translate a message that may come from the API (error details are English strings).
// Like t(), but {name} placeholders can be React elements: tx("Type {slug} to confirm", { slug: <b>{slug}</b> }).
export function tx(s, parts = {}) {
  const out = (typeof s === "string" && catalog[s]) || s;
  const pieces = out.split(/(\{\w+\})/).map((p) => {
    const m = /^\{(\w+)\}$/.exec(p);
    return m && m[1] in parts ? parts[m[1]] : p;
  }).filter((p) => p !== "");
  return createElement(Fragment, null, ...pieces);
}

// Server messages: exact catalog match, else a catalog key with {placeholders} that matches the text
// (e.g. "Seat limit reached ({limit})" matches "Seat limit reached (5)").
let patterns = null;
const reEsc = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
export function tMsg(s) {
  if (typeof s !== "string" || !s) return s;
  if (catalog[s]) return catalog[s];
  if (!patterns) {
    patterns = Object.keys(catalog).filter((k) => k[0] !== "_" && /\{\w+\}/.test(k)).map((k) => {
      const names = [];
      const src = k.split(/(\{\w+\})/).map((part) => {
        const m = /^\{(\w+)\}$/.exec(part);
        if (!m) return reEsc(part);
        names.push(m[1]);
        return "(.+?)";
      }).join("");
      return { k, re: new RegExp(`^${src}$`), names };
    });
  }
  for (const p of patterns) {
    const m = p.re.exec(s);
    if (m) return t(p.k, Object.fromEntries(p.names.map((n, i) => [n, m[i + 1]])));
  }
  return s;
}

export function setLang(l, { reload = true, to } = {}) {
  if (!isLang(l)) return;
  rememberLang(l);
  if (to) window.location.assign(to);
  else if (reload) window.location.reload();
}

// Locale-aware formatting
export const fmtNumber = (n, opts) => (n == null || n === "" ? "" : Number(n).toLocaleString(getLocale(), opts));
export const fmtMoney = (n, currency = "EUR", opts = {}) =>
  n == null || n === "" ? "" : Number(n).toLocaleString(getLocale(), { style: "currency", currency, maximumFractionDigits: Number(n) % 1 ? 2 : 0, ...opts });
const toDate = (d) => (d instanceof Date ? d : new Date(d));
export const fmtDate = (d, opts = { day: "numeric", month: "short", year: "numeric" }) => (d ? toDate(d).toLocaleDateString(getLocale(), { timeZone: "Europe/Madrid", ...opts }) : "");
export const fmtDateTime = (d, opts = { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) => (d ? toDate(d).toLocaleString(getLocale(), { timeZone: "Europe/Madrid", ...opts }) : "");
export const fmtTime = (d, opts = { hour: "2-digit", minute: "2-digit" }) => (d ? toDate(d).toLocaleTimeString(getLocale(), { timeZone: "Europe/Madrid", ...opts }) : "");

// Marketing URL for a language: home "/" ↔ "/es", "/privacy" ↔ "/es/privacy", "/legal" ↔ "/es/aviso-legal".
export function marketingPath(lang, page = "") {
  const slug = SLUGS[lang]?.[page] || page;
  const p = slug ? `/${slug}` : "";
  return lang === "en" ? p || "/" : `/${lang}${p}`;
}

export { LANG_CODES as SUPPORTED_LANGS };
